import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  FileBarChart, Download, FileText, Loader2, Clock, Plus,
  BarChart3, PieChart, Share2, Link2, Copy, Check, ExternalLink,
  Pencil, Trash2, Save, X, ArrowUpRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReportsPage() {
  const [reports, setReports] = useState([]);
  const [projects, setProjects] = useState([]);
  const [vulns, setVulns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showGenerate, setShowGenerate] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [genConfig, setGenConfig] = useState({ project_id: '', report_type: 'executive', format: 'json' });
  const [downloading, setDownloading] = useState({});
  const [sharing, setSharing] = useState({});
  const [shareDialog, setShareDialog] = useState(null);
  const [copied, setCopied] = useState(false);
  // Editing state
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ executive_summary: '', recommendations: '', custom_notes: '' });
  const [savingEdit, setSavingEdit] = useState(false);
  const [detailTab, setDetailTab] = useState('overview');
  const [addFindingVuln, setAddFindingVuln] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [repRes, projRes, vulnRes] = await Promise.all([
          axios.get(`${API}/reports`, { withCredentials: true }),
          axios.get(`${API}/projects`, { withCredentials: true }),
          axios.get(`${API}/vulnerabilities`, { withCredentials: true }),
        ]);
        setReports(repRes.data);
        setProjects(projRes.data);
        setVulns(vulnRes.data);
        if (projRes.data.length > 0) setGenConfig(prev => ({ ...prev, project_id: projRes.data[0].project_id }));
      } catch {} finally { setLoading(false); }
    };
    fetchData();
  }, []);

  const generateReport = async () => {
    if (!genConfig.project_id) return;
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/reports/generate`, genConfig, { withCredentials: true });
      setReports(prev => [{ ...res.data, findings: undefined }, ...prev]);
      setShowGenerate(false);
      toast.success('Report generated');
    } catch { toast.error('Failed to generate report'); } finally { setGenerating(false); }
  };

  const viewReport = async (reportId) => {
    try {
      const res = await axios.get(`${API}/reports/${reportId}`, { withCredentials: true });
      setSelectedReport(res.data);
      setEditForm({ executive_summary: res.data.executive_summary || '', recommendations: res.data.recommendations || '', custom_notes: res.data.custom_notes || '' });
      setEditing(false);
      setDetailTab('overview');
    } catch {}
  };

  const saveEdit = async () => {
    if (!selectedReport) return;
    setSavingEdit(true);
    try {
      const res = await axios.put(`${API}/reports/${selectedReport.report_id}`, editForm, { withCredentials: true });
      setSelectedReport(res.data);
      setEditing(false);
      toast.success('Report updated');
    } catch { toast.error('Failed to save'); } finally { setSavingEdit(false); }
  };

  const deleteReport = async (reportId, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm('Delete this report?')) return;
    try {
      await axios.delete(`${API}/reports/${reportId}`, { withCredentials: true });
      setReports(prev => prev.filter(r => r.report_id !== reportId));
      if (selectedReport?.report_id === reportId) setSelectedReport(null);
      toast.success('Report deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const addFinding = async () => {
    if (!addFindingVuln || !selectedReport) return;
    try {
      const res = await axios.post(`${API}/reports/${selectedReport.report_id}/findings`, { vuln_id: addFindingVuln }, { withCredentials: true });
      setSelectedReport(res.data);
      setAddFindingVuln('');
      toast.success('Finding added to report');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add finding'); }
  };

  const removeFinding = async (vulnId) => {
    if (!selectedReport) return;
    try {
      const res = await axios.delete(`${API}/reports/${selectedReport.report_id}/findings/${vulnId}`, { withCredentials: true });
      setSelectedReport(res.data);
      toast.success('Finding removed');
    } catch { toast.error('Failed to remove'); }
  };

  const downloadPdf = async (reportId, e) => {
    if (e) e.stopPropagation();
    setDownloading(prev => ({ ...prev, [reportId]: true }));
    try {
      const res = await axios.get(`${API}/reports/${reportId}/pdf`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `vaptara_report_${reportId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch { toast.error('Failed to download PDF'); } finally { setDownloading(prev => ({ ...prev, [reportId]: false })); }
  };

  const shareReport = async (reportId, e) => {
    if (e) e.stopPropagation();
    setSharing(prev => ({ ...prev, [reportId]: true }));
    try {
      const res = await axios.post(`${API}/reports/${reportId}/share`, {}, { withCredentials: true });
      const shareToken = res.data.share_token;
      setShareDialog({ reportId, shareToken, shareUrl: `${window.location.origin}/shared-report/${shareToken}`, sharePdfUrl: `${API}/reports/shared/${shareToken}/pdf` });
      setCopied(false);
    } catch { toast.error('Failed to create share link'); } finally { setSharing(prev => ({ ...prev, [reportId]: false })); }
  };

  const copyShareLink = async () => {
    if (!shareDialog) return;
    try { await navigator.clipboard.writeText(shareDialog.shareUrl); setCopied(true); toast.success('Link copied'); setTimeout(() => setCopied(false), 2500); } catch {}
  };

  const reportTypeLabels = { executive: 'Executive Summary', technical: 'Technical Detail', compliance: 'Compliance Report' };
  const reportTypeIcons = { executive: PieChart, technical: FileText, compliance: BarChart3 };

  // Filter vulns not already in report
  const availableVulns = selectedReport ? vulns.filter(v => !(selectedReport.findings || []).some(f => f.vuln_id === v.vuln_id)) : [];

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-heading tracking-tight" data-testid="reports-title">Reports</h1>
          <p className="text-sm text-muted-foreground mt-1">Generate, edit, download, and share reports</p>
        </div>
        <Dialog open={showGenerate} onOpenChange={setShowGenerate}>
          <DialogTrigger asChild><Button className="gap-2" data-testid="generate-report-btn"><Plus className="h-4 w-4" /> Generate Report</Button></DialogTrigger>
          <DialogContent data-testid="generate-report-dialog">
            <DialogHeader><DialogTitle>Generate Report</DialogTitle></DialogHeader>
            <div className="space-y-4 pt-4">
              <div><Label>Project</Label>
                <Select value={genConfig.project_id} onValueChange={v => setGenConfig(p => ({ ...p, project_id: v }))}>
                  <SelectTrigger data-testid="report-project-select"><SelectValue placeholder="Select project" /></SelectTrigger>
                  <SelectContent>{projects.map(p => <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label>Report Type</Label>
                <Select value={genConfig.report_type} onValueChange={v => setGenConfig(p => ({ ...p, report_type: v }))}>
                  <SelectTrigger data-testid="report-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="executive">Executive Summary</SelectItem><SelectItem value="technical">Technical Detail</SelectItem><SelectItem value="compliance">Compliance Report</SelectItem></SelectContent>
                </Select></div>
              <Button onClick={generateReport} className="w-full" disabled={generating || !genConfig.project_id} data-testid="gen-report-submit">
                {generating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Generate Report
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Reports List */}
      <Card data-testid="reports-list-card"><CardContent className="p-0"><ScrollArea className="h-[500px]">
        {loading ? <div className="flex items-center justify-center h-[400px]"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
         : reports.length === 0 ? <div className="flex flex-col items-center justify-center h-[400px]"><FileBarChart className="h-10 w-10 text-muted-foreground/30 mb-3" /><p className="text-sm text-muted-foreground">No reports yet.</p></div>
         : <div className="divide-y">{reports.map(report => {
           const Icon = reportTypeIcons[report.report_type] || FileText;
           return (
             <div key={report.report_id} className="flex items-center gap-4 p-4 hover:bg-muted/30 transition-colors cursor-pointer" onClick={() => viewReport(report.report_id)} data-testid={`report-${report.report_id}`}>
               <div className="p-2 rounded-lg bg-primary/10"><Icon className="h-5 w-5 text-primary" /></div>
               <div className="flex-1 min-w-0">
                 <p className="font-medium text-sm">{reportTypeLabels[report.report_type]} - {report.project_name}</p>
                 <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                   <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{new Date(report.generated_at).toLocaleString()}</span>
                   {report.summary && <span>{report.summary.total_vulnerabilities} findings</span>}
                 </div>
               </div>
               <Badge variant="outline" className="text-xs capitalize hidden sm:inline-flex">{report.report_type}</Badge>
               <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={(e) => downloadPdf(report.report_id, e)} disabled={downloading[report.report_id]} data-testid={`download-pdf-${report.report_id}`}>
                 {downloading[report.report_id] ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
               </Button>
               <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={(e) => shareReport(report.report_id, e)} disabled={sharing[report.report_id]} data-testid={`share-report-${report.report_id}`}>
                 {sharing[report.report_id] ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />}
               </Button>
               <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0 text-destructive" onClick={(e) => deleteReport(report.report_id, e)} data-testid={`delete-report-${report.report_id}`}>
                 <Trash2 className="h-4 w-4" />
               </Button>
             </div>
           );
         })}</div>}
      </ScrollArea></CardContent></Card>

      {/* Report Detail Dialog */}
      <Dialog open={!!selectedReport} onOpenChange={() => setSelectedReport(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="report-detail-dialog">
          {selectedReport && (<>
            <DialogHeader className="shrink-0">
              <div className="flex items-center justify-between gap-4">
                <DialogTitle className="font-heading">{reportTypeLabels[selectedReport.report_type]} - {selectedReport.project_name}</DialogTitle>
                <div className="flex items-center gap-2 shrink-0">
                  {!editing && <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setEditing(true)} data-testid="edit-report-btn"><Pencil className="h-3.5 w-3.5" /> Edit</Button>}
                  {editing && <>
                    <Button size="sm" className="gap-1.5" onClick={saveEdit} disabled={savingEdit} data-testid="save-report-btn">{savingEdit ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save</Button>
                    <Button variant="ghost" size="sm" onClick={() => setEditing(false)} data-testid="cancel-edit-btn"><X className="h-3.5 w-3.5" /></Button>
                  </>}
                  <Button variant="outline" size="sm" className="gap-1.5" onClick={() => downloadPdf(selectedReport.report_id)} disabled={downloading[selectedReport.report_id]} data-testid="detail-download-pdf-btn">
                    {downloading[selectedReport.report_id] ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} PDF
                  </Button>
                  <Button variant="outline" size="sm" className="gap-1.5" onClick={() => shareReport(selectedReport.report_id)} disabled={sharing[selectedReport.report_id]} data-testid="detail-share-btn">
                    {sharing[selectedReport.report_id] ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Share2 className="h-3.5 w-3.5" />} Share
                  </Button>
                </div>
              </div>
            </DialogHeader>

            <Tabs value={detailTab} onValueChange={setDetailTab} className="flex-1 overflow-hidden flex flex-col">
              <TabsList className="shrink-0" data-testid="report-detail-tabs">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="findings">Findings ({selectedReport.findings?.length || 0})</TabsTrigger>
                <TabsTrigger value="content">Content</TabsTrigger>
              </TabsList>

              <ScrollArea className="flex-1 mt-3">
                <TabsContent value="overview" className="mt-0">
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="p-3 rounded-lg bg-muted/50 text-center"><p className="text-2xl font-bold">{selectedReport.summary?.total_vulnerabilities}</p><p className="text-xs text-muted-foreground">Total Findings</p></div>
                      <div className="p-3 rounded-lg bg-red-500/10 text-center"><p className="text-2xl font-bold text-red-500">{selectedReport.summary?.severity_distribution?.critical || 0}</p><p className="text-xs text-muted-foreground">Critical</p></div>
                      <div className="p-3 rounded-lg bg-orange-500/10 text-center"><p className="text-2xl font-bold text-orange-500">{selectedReport.summary?.severity_distribution?.high || 0}</p><p className="text-xs text-muted-foreground">High</p></div>
                      <div className="p-3 rounded-lg bg-muted/50 text-center"><p className="text-2xl font-bold">{selectedReport.summary?.risk_score}</p><p className="text-xs text-muted-foreground">Risk Score</p></div>
                    </div>
                    {selectedReport.compliance_alignment?.length > 0 && <div><p className="text-sm font-medium mb-2">Compliance</p><div className="flex flex-wrap gap-2">{selectedReport.compliance_alignment.map(fw => <Badge key={fw} variant="outline">{fw}</Badge>)}</div></div>}
                  </div>
                </TabsContent>

                <TabsContent value="findings" className="mt-0">
                  <div className="space-y-3">
                    {/* Add finding */}
                    <div className="flex gap-2 items-end sticky top-0 bg-background z-10 pb-2">
                      <div className="flex-1">
                        <Label className="text-xs">Add vulnerability to report</Label>
                        <Select value={addFindingVuln} onValueChange={setAddFindingVuln}>
                          <SelectTrigger data-testid="add-finding-select"><SelectValue placeholder="Select vulnerability..." /></SelectTrigger>
                          <SelectContent>{availableVulns.map(v => <SelectItem key={v.vuln_id} value={v.vuln_id}>{v.severity.toUpperCase()} - {v.title}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <Button size="sm" onClick={addFinding} disabled={!addFindingVuln} data-testid="add-finding-btn"><Plus className="h-4 w-4" /></Button>
                    </div>
                    <Separator />
                    <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
                      {(selectedReport.findings || []).map(f => (
                        <div key={f.vuln_id} className="p-3 rounded-lg border flex items-start gap-3" data-testid={`report-finding-${f.vuln_id}`}>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant="outline" className={`text-xs ${f.severity === 'critical' ? 'border-red-500/30 text-red-500' : f.severity === 'high' ? 'border-orange-500/30 text-orange-500' : f.severity === 'medium' ? 'border-yellow-500/30 text-yellow-500' : f.severity === 'informational' ? 'border-blue-500/30 text-blue-500' : 'border-green-500/30 text-green-500'}`}>{f.severity}</Badge>
                              <span className="text-sm font-medium truncate">{f.title}</span>
                              <span className="text-xs text-muted-foreground font-mono ml-auto">{f.cvss_score}</span>
                            </div>
                            <p className="text-xs text-muted-foreground line-clamp-2">{f.description}</p>
                          </div>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive shrink-0" onClick={() => removeFinding(f.vuln_id)} data-testid={`remove-finding-${f.vuln_id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      ))}
                    </div>
                    {(!selectedReport.findings || selectedReport.findings.length === 0) && <p className="text-sm text-muted-foreground text-center py-6">No findings in this report.</p>}
                  </div>
                </TabsContent>

                <TabsContent value="content" className="mt-0">
                  <div className="space-y-4">
                    <div>
                      <Label className="mb-1.5 block">Executive Summary</Label>
                      {editing ? <Textarea value={editForm.executive_summary} onChange={e => setEditForm(p => ({ ...p, executive_summary: e.target.value }))} rows={4} placeholder="Write an executive summary..." data-testid="edit-exec-summary" />
                       : <div className="p-3 rounded-lg bg-muted/30 min-h-[60px]"><p className="text-sm text-muted-foreground whitespace-pre-wrap">{selectedReport.executive_summary || 'No executive summary yet. Click Edit to add one.'}</p></div>}
                    </div>
                    <div>
                      <Label className="mb-1.5 block">Recommendations</Label>
                      {editing ? <Textarea value={editForm.recommendations} onChange={e => setEditForm(p => ({ ...p, recommendations: e.target.value }))} rows={4} placeholder="Add remediation recommendations..." data-testid="edit-recommendations" />
                       : <div className="p-3 rounded-lg bg-muted/30 min-h-[60px]"><p className="text-sm text-muted-foreground whitespace-pre-wrap">{selectedReport.recommendations || 'No recommendations yet.'}</p></div>}
                    </div>
                    <div>
                      <Label className="mb-1.5 block">Additional Notes</Label>
                      {editing ? <Textarea value={editForm.custom_notes} onChange={e => setEditForm(p => ({ ...p, custom_notes: e.target.value }))} rows={3} placeholder="Any additional notes..." data-testid="edit-custom-notes" />
                       : <div className="p-3 rounded-lg bg-muted/30 min-h-[60px]"><p className="text-sm text-muted-foreground whitespace-pre-wrap">{selectedReport.custom_notes || 'No notes yet.'}</p></div>}
                    </div>
                  </div>
                </TabsContent>
              </ScrollArea>
            </Tabs>
          </>)}
        </DialogContent>
      </Dialog>

      {/* Share Dialog */}
      <Dialog open={!!shareDialog} onOpenChange={() => setShareDialog(null)}>
        <DialogContent className="max-w-lg" data-testid="share-dialog">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Share2 className="h-5 w-5 text-primary" /> Share Report</DialogTitle></DialogHeader>
          {shareDialog && (
            <div className="space-y-5 mt-2">
              <div>
                <Label className="text-xs text-muted-foreground mb-2 block">Share Link (30 days)</Label>
                <div className="flex items-center gap-2">
                  <Input readOnly value={shareDialog.shareUrl} className="font-mono text-xs flex-1" data-testid="share-link-input" />
                  <Button variant="outline" size="icon" onClick={copyShareLink} data-testid="copy-share-link-btn">{copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}</Button>
                </div>
              </div>
              <Separator />
              <div>
                <Label className="text-xs text-muted-foreground mb-2 block">Direct PDF Link</Label>
                <div className="flex items-center gap-2">
                  <Input readOnly value={shareDialog.sharePdfUrl} className="font-mono text-xs flex-1" data-testid="share-pdf-link-input" />
                  <Button variant="outline" size="icon" onClick={async () => { try { await navigator.clipboard.writeText(shareDialog.sharePdfUrl); toast.success('Copied'); } catch {} }} data-testid="copy-pdf-link-btn"><Copy className="h-4 w-4" /></Button>
                </div>
              </div>
              <div className="p-3 rounded-lg bg-primary/5 border border-primary/20 text-xs text-muted-foreground">
                <p className="font-medium text-foreground mb-1">Sharing Info</p>
                <p>Anyone with this link can view the report and download PDF. Expires in 30 days.</p>
              </div>
              <div className="flex gap-2">
                <Button className="flex-1 gap-2" onClick={() => window.open(shareDialog.shareUrl, '_blank')} data-testid="open-shared-report-btn"><ExternalLink className="h-4 w-4" /> Open</Button>
                <Button variant="outline" className="flex-1 gap-2" onClick={() => window.open(shareDialog.sharePdfUrl, '_blank')} data-testid="download-shared-pdf-btn"><Download className="h-4 w-4" /> PDF</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
